import React, { useState, useEffect, useContext, useCallback } from 'react';
import LocalModeBanner from './LocalModeBanner';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { useTheme, ThemedCard, ThemedButton, ThemedContainer, ThemedInlay } from './ThemeContext';
import { createGameSocket } from './GameSocketProvider';
import LocalGamesManager from './LocalGamesManager';
import WaitingGameSuggestion from './WaitingGameSuggestion';

// Component for bot slot selection
const BotSlotSelect = ({ label, value, onChange, selectedValues, disabled = false, isAnarchySlot = false, isAnarchyMode = false }) => {
  const { woodStyles } = useTheme();

  // Filter out options that are already selected in other slots
  // (except 'random' which can be used multiple times)
  const getAvailableOptions = () => {
    const options = [
      { value: 'random', label: 'Random Move' },
      { value: '1', label: 'Stockfish (Top Line)' },
      { value: '2', label: 'Stockfish (Second Line)' },
      { value: '3', label: 'Stockfish (Third Line)' }
    ];

    // If this is an anarchy slot and anarchy mode is enabled, add anarchy option
    if (isAnarchySlot && isAnarchyMode) {
      options.push({ value: 'anarchy', label: 'Anarchy Move ' });
    }

    // If not disabled, filter out options already selected
    if (!disabled) {
      return options.filter(option =>
        option.value === 'random' || // Random is always available
        option.value === value || // Current value is available
        !selectedValues.includes(option.value) // Options not selected elsewhere
      );
    }

    return options;
  };

  return (
    <div>
      <label className={`block text-sm mb-2 ${woodStyles.text.secondary}`}>
        {label}:
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full p-2 rounded 
          ${woodStyles.inlay.background} 
          ${woodStyles.text.primary} 
          ${woodStyles.card.border}
          border focus:outline-none focus:ring-2 focus:ring-blue-500
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {getAvailableOptions().map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const ChessLobby = () => {
  const { isDark, woodStyles } = useTheme();
  const { user, isLocalOnly } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [availableGames, setAvailableGames] = useState([]);
  const [activeGames, setActiveGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBotModalOpen, setIsBotModalOpen] = useState(false);
  const [timeControl, setTimeControl] = useState('60');
  const [preferredColor, setPreferredColor] = useState('random');
  const [isAnarchy, setIsAnarchy] = useState(false);

  const [isGamesLoading, setIsGamesLoading] = useState(false);
  const [isActiveGamesLoading, setIsActiveGamesLoading] = useState(false);


  // Bot game configuration
  const [botSlot1, setBotSlot1] = useState('random');
  const [botSlot2, setBotSlot2] = useState('random');
  const [botSlot3, setBotSlot3] = useState('random');

  // For waiting game suggestion
  const [waitingGame, setWaitingGame] = useState(null);
  const [showSuggestion, setShowSuggestion] = useState(false);

  // Check for active bot games
  const [hasActiveBotGame, setHasActiveBotGame] = useState(false);
  const [activeBotGameId, setActiveBotGameId] = useState(null);

  // Load bot game status
  useEffect(() => {
    // Check if there's an active bot game
    const checkActiveBotGames = () => {
      let activeId = null;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith('chess_game_local_')) {
          try {
            const gameData = JSON.parse(localStorage.getItem(key));
            if (gameData && gameData.createdBy === user.id && gameData.status === 'active') {
              activeId = key.replace('chess_game_', '');
              break;
            }
          } catch (error) {
            console.error('Error parsing game data:', error);
          }
        }
      }

      setHasActiveBotGame(!!activeId);
      setActiveBotGameId(activeId);
    };

    checkActiveBotGames();

    // Listen for storage changes and game deletion events
    const handleStorageChange = () => {
      checkActiveBotGames();
    };

    const handleGameDeleted = () => {
      checkActiveBotGames();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('bot-game-deleted', handleGameDeleted);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('bot-game-deleted', handleGameDeleted);
    };
  }, [user.id]);

  const fetchGames = useCallback(async () => {
    try {
      // If user is in local-only mode, return empty arrays
      if (user?.isLocalOnly) {
        setAvailableGames([]);
        setActiveGames([]);
        setIsLoading(false);
        return;
      }

      // Only show loading spinner in the respective sections
      setIsGamesLoading(true);
      setIsActiveGamesLoading(true);

      try {
        const [availableRes, activeRes] = await Promise.all([
          axios.get('/api/games'),
          axios.get(`/api/users/${user.id}/games`)
        ]);

        // Ensure we're working with arrays
        const availableGamesData = Array.isArray(availableRes.data)
          ? availableRes.data
          : (availableRes.data?.data || []);

        const activeGamesData = Array.isArray(activeRes.data)
          ? activeRes.data
          : (activeRes.data?.data || []);
       
        setAvailableGames(availableGamesData,activeRes.data);

        // Filter active games
        setActiveGames(
          Array.isArray(activeGamesData)
            ? activeGamesData.filter(game => game.status === 'waiting' || game.status === 'active')
            : []
        );
        
      } catch (error) {
        console.error('Error fetching games:', error);
        setError('Failed to load games. Please try again.');
        // Set empty arrays on error
        setAvailableGames([]);
        setActiveGames([]);
      } finally {
        setIsGamesLoading(false);
        setIsActiveGamesLoading(false);
        setIsLoading(false); // Main loading state
      }
    } catch (error) {
      console.error('Error in fetchGames:', error);
      setIsGamesLoading(false);
      setIsActiveGamesLoading(false);
      setIsLoading(false);
      setError('An unexpected error occurred');
    }
  }, [user.id, user?.isLocalOnly]);

  useEffect(() => {
    if (isAnarchy) {
      setBotSlot3('anarchy');
    } else if (botSlot3 === 'anarchy') {

      setBotSlot3('random');
    }
  }, [isAnarchy, botSlot3]);

  useEffect(() => {
    // Initial fetch
    fetchGames();
    
    // Create a refresh interval with a longer delay to avoid excessive refreshes
    const interval = setInterval(fetchGames, 10000); // 10 seconds
    
    // Clean up on unmount
    return () => clearInterval(interval);
  }, [fetchGames]);

  // Update waiting game for suggestion
  useEffect(() => {
    // Find the oldest waiting game created by the user
    const waitingGames = activeGames.filter(
      game => game.status === 'waiting' && game.creator.id === user.id
    );

    if (waitingGames.length > 0) {
      // Sort by creation time, oldest first
      waitingGames.sort((a, b) =>
        new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
      );
      setWaitingGame(waitingGames[0]);
      setShowSuggestion(true);
    } else {
      setWaitingGame(null);
      setShowSuggestion(false);
    }
  }, [activeGames, user.id]);
  useEffect(() => {
    // Check for the openBotModal query parameter
    const params = new URLSearchParams(location.search);
    if (params.get('openBotModal') === 'true') {
      // Open the bot modal without auto-populating settings
      setIsBotModalOpen(true);

      // Clean up the URL to remove the query parameter
      navigate('/lobby', { replace: true });
    }
  }, [location, navigate]);
  const handleCreateGame = async (settings) => {
    try {
      const response = await axios.post('/api/games/create', {
        userId: user.id,
        username: user.username || user.id,
        ...settings
      });

      navigate(`/game/${response.data.id}`);
    } catch (error) {
      if (error.response?.data?.gameId) {
        navigate(`/game/${error.response.data.gameId}`);
      } else {
        setError(error.response?.data?.error || 'Failed to create game');
      }
    }
  };

  const handleCreateBotGame = (waitingGameToAbort = null) => {
    // If there's an active bot game, redirect to it
    if (hasActiveBotGame && activeBotGameId) {
      navigate(`/game_local/${activeBotGameId}`);
      return;
    }

    // Generate a unique ID for the local game
    const localGameId = `local_${Math.random().toString(36).substr(2, 9)}`;

    // Determine actual player color
    const actualPlayerColor = preferredColor === 'random'
      ? Math.random() < 0.5 ? 'white' : 'black'
      : preferredColor;

    // Save bot configuration to localStorage
    const botConfig = {
      preferredColor,
      isAnarchy,
      botSlotConfig: {
        slot1: botSlot1,
        slot2: botSlot2,
        slot3: botSlot3
      },
      createdAt: Date.now(),
      createdBy: user.id,
      playerColor: actualPlayerColor,
      status: 'active',
      moves: [],
      fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' // Initial position
    };

    localStorage.setItem(`chess_game_${localGameId}`, JSON.stringify(botConfig));

    // If switching from a waiting game, abort it
    if (waitingGameToAbort) {
      handleAbortGame(waitingGameToAbort.id);
    }

    // Navigate to the local game
    navigate(`/game_local/${localGameId}`);
  };
  // Handler for switching from a waiting game to a bot game
  const handleSwitchToBotGame = (game) => {
    // Open bot game modal pre-configured with the waiting game's settings
    setTimeControl(game.timeControl || '60');
    setPreferredColor(game.preferredColor || 'random');
    setIsAnarchy(game.isAnarchy || false);
    setBotSlot1('random');
    setBotSlot2('random');
    setBotSlot3(game.isAnarchy ? 'anarchy' : 'random');

    // Store the game to abort
    setWaitingGame(game);

    // Open the bot game modal
    setIsBotModalOpen(true);
  };

  const handleJoinGame = async (gameId) => {
    try {
      await axios.post(`/api/games/${gameId}/join`, {
        userId: user.id,
        username: user.username || user.id
      });
      navigate(`/game/${gameId}`);
    } catch (error) {
      if (error.response?.data?.gameId) {
        navigate(`/game/${error.response.data.gameId}`);
      } else {
        setError(error.response?.data?.error || 'Failed to join game');
      }
    }
  };

  const handleAbortGame = async (gameId) => {
    try {
      // First, show confirmation dialog
      if (!window.confirm("Are you sure you want to abort this game?")) {
        return; // User canceled
      }
  
      // For network games, use the game server to handle the abort via socket
      // Create a temporary loading state for this specific game
      setActiveGames(prev =>
        prev.map(game =>
          game.id === gameId
            ? { ...game, isAborting: true }
            : game
        )
      );
  
      // Connect to the game server via socket and handle the abort there
      console.log(`Creating socket to abort game ${gameId}`);
      const tempSocket = createGameSocket(gameId, user);
  
      // Set up a timeout to handle lack of response
      const abortTimeout = setTimeout(() => {
        console.log(`Abort timeout for game ${gameId}, forcing refresh`);
        tempSocket.disconnect();
        fetchGames(); // Refresh games list anyway
      }, 5000);
  
      // Set up connection and event handlers
      tempSocket.on('connect', () => {
        console.log(`Connected to game ${gameId}, sending abort request`);
        // Once connected, emit abort request
        tempSocket.emit('abort_game', { gameId });
      });
  
      // Listen for game_end event to confirm the abort
      tempSocket.on('game_end', (result) => {
        console.log(`Received game_end for abort of ${gameId}:`, result);
        clearTimeout(abortTimeout);
        
        if (result.type === 'aborted') {
          // Successful abort - refresh games list
          fetchGames();
        }
  
        // Clean up socket
        tempSocket.disconnect();
      });
  
      // Handle potential errors
      tempSocket.on('game_error', (error) => {
        console.error(`Socket error during abort of ${gameId}:`, error);
        clearTimeout(abortTimeout);
        setError(error.message || 'Failed to abort game');
  
        // Remove aborting state
        setActiveGames(prev =>
          prev.map(game =>
            game.id === gameId
              ? { ...game, isAborting: false }
              : game
          )
        );
  
        // Clean up socket
        tempSocket.disconnect();
        
        // Refresh games list anyway after error
        fetchGames();
      });
  
    } catch (error) {
      console.error(`Game abort error for ${gameId}:`, error);
      setError(error.response?.data?.error || 'Failed to abort game');
  
      // Remove aborting state if there was an error
      setActiveGames(prev =>
        prev.map(game =>
          game.id === gameId
            ? { ...game, isAborting: false }
            : game
        )
      );
      
      // Refresh games list anyway after error
      fetchGames();
    }
  };

  const handleQuickJoin = async () => {
    try {
      // Filter games that can be joined
      const joinableGames = availableGames.filter(game =>
        game.status === 'waiting' && game.creator.id !== user.id
      );

      if (joinableGames.length === 0) {
        setError('No available games to join');
        return;
      }

      // Randomly select a game
      const randomGame = joinableGames[Math.floor(Math.random() * joinableGames.length)];

      await handleJoinGame(randomGame.id);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to quick join game');
    }
  };

  const handlePlayBot = () => {
    // If there's an active bot game, redirect to it
    if (hasActiveBotGame && activeBotGameId) {
      navigate(`/game_local/${activeBotGameId}`);
      return;
    }

    // Otherwise open the bot game creation modal
    setIsBotModalOpen(true);
  };

  if (isLoading) {
    return (
      <ThemedContainer className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
      </ThemedContainer>
    );
  }

  return (
    <ThemedContainer className="max-w-6xl mx-auto p-4">
      {/* Local Mode Banner */}
      <LocalModeBanner />


      {/* Waiting game suggestion */}
      {showSuggestion && waitingGame && (
        <WaitingGameSuggestion
          game={waitingGame}
          onSwitchToBotGame={handleSwitchToBotGame}
          onClose={() => setShowSuggestion(false)}
          inGamePage={false}
        />
      )}

      {/* Display local bot games */}
      <LocalGamesManager user={user} />

      {activeGames.length > 0 && (
  <ThemedCard className="mb-8">
    <div className={`px-3 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
      <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
        Your Active Games
      </span>
    </div>

    <div className="p-4">
      {isActiveGamesLoading ? (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {activeGames.map((game) => (
            <div key={game.id} className={`p-3 rounded-lg ${woodStyles.surface.secondary}`}>
              <div className="flex justify-between items-center">
                <div>
                  <div className={`font-medium ${woodStyles.text.primary}`}>
                    Game #{game.id.slice(5)}
                  </div>
                  <div className={`text-sm ${woodStyles.text.secondary}`}>
                    {game.timeControl}s per move • {game.isAborting ? 'Aborting...' : game.status}
                  </div>
                </div>
                <div className="flex gap-2 z-10">
                  <ThemedButton
                    onClick={() => navigate(`/game/${game.id}`)}
                    variant="primary"
                    disabled={game.isAborting}
                  >
                    {game.status === 'waiting' ? 'View' : 'Play'}
                  </ThemedButton>
                  {game.status === 'waiting' && game.creator.id === user.id && (
                    <ThemedButton
                      onClick={() => handleAbortGame(game.id)}
                      variant="secondary"
                      disabled={game.isAborting}
                    >
                      {game.isAborting ? 'Aborting...' : 'Abort'}
                    </ThemedButton>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </ThemedCard>
)}

      {/* Game Creation Buttons */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ThemedButton
          onClick={() => setIsModalOpen(true)}
          disabled={activeGames.length > 0 || hasActiveBotGame || isLocalOnly}
          className="w-full"
        >
          {activeGames.length > 0 ? 'Finish active game first' :
            hasActiveBotGame ? 'Finish active bot game ' :
              isLocalOnly ? 'Unavailable in Local Mode' : 'Create New Game'}
        </ThemedButton>

        <ThemedButton
          onClick={handleQuickJoin}
          disabled={activeGames.length > 0 || hasActiveBotGame || isLocalOnly}
          variant="secondary"
          className="w-full"
        >
          {activeGames.length > 0 ? 'Finish active game first' :
            hasActiveBotGame ? 'Finish active bot game ' :
              isLocalOnly ? 'Unavailable in Local Mode' : 'Quick Join'}
        </ThemedButton>

        <ThemedButton
          onClick={handlePlayBot}
          disabled={activeGames.length > 0}
          variant="secondary"
          className="w-full"
        >
          {activeGames.length > 0
            ? 'Finish active game first'
            : hasActiveBotGame
              ? 'Continue Bot Game'
              : 'Play vs Bot'}
        </ThemedButton>
      </div>

      {/* Available Games */}
      <ThemedCard>
        <div className={`px-3 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
          <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            Available Games
          </span>
        </div>

        <div className="p-4">
          {isGamesLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {availableGames.map((game) => {
                // Determine the color to join
                const colorToJoin = !game.players?.white ? 'white' : 'black';

                return (
                  <div key={game.id} className={`p-3 rounded-lg ${woodStyles.surface.secondary}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          Game #{game.id.slice(5)}
                        </div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {game.timeControl}s • {game.isAnarchy ? 'Anarchy' : 'Normal'}
                          {` • Created by ${game.creator.username || game.creator.id}`}
                        </div>
                      </div>
                      <ThemedButton
                        onClick={() => handleJoinGame(game.id)}
                        disabled={activeGames.length > 0}
                        variant="primary"
                      >
                        {activeGames.length > 0
                          ? 'Finish active game first'
                          : `Play as ${colorToJoin === 'white' ? 'White' : 'Black'}`}
                      </ThemedButton>
                    </div>
                  </div>
                );
              })}

              {availableGames.length === 0 && (
                <div className={`text-center p-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  No games available. Create one to get started!
                </div>
              )}
            </div>
          )}
        </div>
      </ThemedCard>

      {/* Regular Game Creation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ThemedCard className="max-w-md w-full mx-4">
            <div className={`px-3 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
              <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                Create New Game
              </span>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Time Control (seconds per move)
                  </label>
                  <select
                    value={timeControl}
                    onChange={(e) => setTimeControl(e.target.value)}
                    className={`w-full p-2 rounded 
                      ${woodStyles.inlay.background} 
                      ${woodStyles.text.primary} 
                      ${woodStyles.card.border}
                      border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="15">15 seconds</option>
                    <option value="30">30 seconds</option>
                    <option value="45">45 seconds</option>
                    <option value="60">60 seconds</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Preferred Color
                  </label>
                  <select
                    value={preferredColor}
                    onChange={(e) => setPreferredColor(e.target.value)}
                    className={`w-full p-2 rounded 
                      ${woodStyles.inlay.background} 
                      ${woodStyles.text.primary} 
                      ${woodStyles.card.border}
                      border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="random">Random</option>
                    <option value="white">White</option>
                    <option value="black">Black</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="anarchy-mode"
                    checked={isAnarchy}
                    onChange={(e) => setIsAnarchy(e.target.checked)}
                    className={`h-4 w-4 rounded ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                  />
                  <label htmlFor="anarchy-mode" className={`ml-2 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Anarchy Mode
                  </label>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <ThemedButton
                    onClick={() => setIsModalOpen(false)}
                    variant="secondary"
                  >
                    Cancel
                  </ThemedButton>
                  <ThemedButton
                    onClick={() => {
                      handleCreateGame({
                        timeControl: parseInt(timeControl),
                        preferredColor,
                        isAnarchy
                      });
                      setIsModalOpen(false);
                    }}
                    variant="primary"
                  >
                    Create Game
                  </ThemedButton>
                </div>
              </div>
            </div>
          </ThemedCard>
        </div>
      )}

      {/* Bot Game Creation Modal */}
      {isBotModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ThemedCard className="max-w-md w-full mx-4">
            <div className={`px-3 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
              <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                Create Bot Game
              </span>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Preferred Color
                  </label>
                  <select
                    value={preferredColor}
                    onChange={(e) => setPreferredColor(e.target.value)}
                    className={`w-full p-2 rounded 
                ${woodStyles.inlay.background} 
                ${woodStyles.text.primary} 
                ${woodStyles.card.border}
                border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="random">Random</option>
                    <option value="white">White</option>
                    <option value="black">Black</option>
                  </select>
                </div>

                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="bot-anarchy-mode"
                    checked={isAnarchy}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setIsAnarchy(newValue);
                      // If anarchy is enabled, set slot 3 to anarchy
                      if (newValue) {
                        setBotSlot3('anarchy');
                      } else {
                        // If anarchy is disabled, reset slot 3 to random
                        setBotSlot3('random');
                      }
                    }}
                    className={`h-4 w-4 rounded ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                  />
                  <label htmlFor="bot-anarchy-mode" className={`ml-2 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Anarchy Mode
                  </label>
                </div>

                <div className="border-t pt-4 border-gray-700">
                  <h3 className={`text-sm font-medium mb-3 ${woodStyles.text.primary}`}>
                    Bot Slot Configuration
                  </h3>

                  <div className="space-y-4">
                    {/* Get currently selected values for filtering */}
                    {(() => {
                      const selectedValues = [botSlot1, botSlot2, botSlot3].filter(
                        val => val !== 'random' && val !== 'anarchy'
                      );

                      return (
                        <>
                          <BotSlotSelect
                            label="Slot 1"
                            value={botSlot1}
                            onChange={setBotSlot1}
                            selectedValues={selectedValues}
                            isAnarchyMode={isAnarchy}
                          />

                          <BotSlotSelect
                            label="Slot 2"
                            value={botSlot2}
                            onChange={setBotSlot2}
                            selectedValues={selectedValues}
                            isAnarchyMode={isAnarchy}
                          />

                          <BotSlotSelect
                            label="Slot 3"
                            value={botSlot3}
                            onChange={setBotSlot3}
                            selectedValues={selectedValues}
                            disabled={isAnarchy} // Disable if in anarchy mode
                            isAnarchySlot={true} // This is the slot that can be set to anarchy
                            isAnarchyMode={isAnarchy}
                          />
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="text-xs text-gray-500 italic mt-2">
                  Note: Bot games are local and will not be saved to the server or recorded in your game history.
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <ThemedButton
                    onClick={() => setIsBotModalOpen(false)}
                    variant="secondary"
                  >
                    Cancel
                  </ThemedButton>
                  <ThemedButton
                    onClick={() => {
                      handleCreateBotGame();
                      setIsBotModalOpen(false);
                    }}
                    variant="primary"
                  >
                    Create Bot Game
                  </ThemedButton>
                </div>
              </div>
            </div>
          </ThemedCard>
        </div>
      )}
    </ThemedContainer>
  );
};

export default ChessLobby;